// Jira Token Expiry Detection Service
// Detects expired/invalid Jira tokens and prompts users to refresh them

import { validateJiraCredentials } from "./jira-service";
import { atlassianOAuth2 } from "./atlassian-oauth2";

export interface TokenExpiryInfo {
  isExpired: boolean;
  error?: string;
  lastChecked: string;
}

export interface JiraAuthError {
  type: 'expired' | 'invalid' | 'network' | 'unknown';
  message: string;
  originalError?: string;
}

// Simple settings interface for token expiry checks
export interface JiraCredentials {
  domain: string;
  email: string;
  apiToken: string;
  /** Atlassian OAuth2 access token — when present, used instead of Basic auth */
  oauthToken?: string;
}

// Token expiry cache to avoid repeated checks
const TOKEN_EXPIRY_CACHE_KEY = 'verigit-jira-token-expiry';
const TOKEN_EXPIRY_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

class JiraTokenExpiryService {
  private static instance: JiraTokenExpiryService;
  private expiryCallbacks: Set<(error: JiraAuthError) => void> = new Set();

  private constructor() {}

  static getInstance(): JiraTokenExpiryService {
    if (!JiraTokenExpiryService.instance) {
      JiraTokenExpiryService.instance = new JiraTokenExpiryService();
    }
    return JiraTokenExpiryService.instance;
  }

  // Register callback for token expiry notifications
  onTokenExpired(callback: (error: JiraAuthError) => void): () => void {
    this.expiryCallbacks.add(callback);
    return () => this.expiryCallbacks.delete(callback);
  }

  // Notify all registered callbacks
  private notifyExpiry(error: JiraAuthError): void {
    this.expiryCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (err) {
        console.error('[JiraTokenExpiry] Error in expiry callback:', err);
      }
    });
  }

  // Check if cached expiry info is still valid
  private isCacheValid(cached: TokenExpiryInfo): boolean {
    return Date.now() - new Date(cached.lastChecked).getTime() < TOKEN_EXPIRY_CACHE_TTL;
  }

  // Get cached expiry info
  private getCachedExpiry(): TokenExpiryInfo | null {
    try {
      const cached = localStorage.getItem(TOKEN_EXPIRY_CACHE_KEY);
      if (!cached) return null;
      
      const expiryInfo = JSON.parse(cached) as TokenExpiryInfo;
      if (this.isCacheValid(expiryInfo)) {
        return expiryInfo;
      }
    } catch (err) {
      console.warn('[JiraTokenExpiry] Failed to parse cached expiry info:', err);
    }
    return null;
  }

  // Cache expiry info
  private setCachedExpiry(info: TokenExpiryInfo): void {
    try {
      localStorage.setItem(TOKEN_EXPIRY_CACHE_KEY, JSON.stringify(info));
    } catch (err) {
      console.warn('[JiraTokenExpiry] Failed to cache expiry info:', err);
    }
  }

  // Clear cached expiry info
  clearCachedExpiry(): void {
    localStorage.removeItem(TOKEN_EXPIRY_CACHE_KEY);
  }

  // Detect if error message indicates token expiry
  private detectExpiryType(error: string): JiraAuthError['type'] {
    const lowerError = error.toLowerCase();
    
    // Common Jira token expiry indicators
    if (
      lowerError.includes('401') ||
      lowerError.includes('unauthorized') ||
      lowerError.includes('invalid') && lowerError.includes('token') ||
      lowerError.includes('expired') ||
      lowerError.includes('authentication failed') ||
      lowerError.includes('basic auth failed') ||
      lowerError.includes('bearer also failed')
    ) {
      return 'expired';
    }
    
    // Network-related errors
    if (
      lowerError.includes('network') ||
      lowerError.includes('fetch') ||
      lowerError.includes('cors') ||
      lowerError.includes('timeout')
    ) {
      return 'network';
    }
    
    return 'unknown';
  }

  // Validate Jira credentials and detect expiry
  async validateAndDetectExpiry(settings: JiraCredentials): Promise<{
    valid: boolean;
    expiryInfo?: TokenExpiryInfo;
    error?: JiraAuthError;
  }> {
    // When OAuth2 is active, skip Basic-auth validation — token management is
    // handled by atlassianOAuth2.getAccessToken() which auto-refreshes.
    const oauthToken = settings.oauthToken ?? (await atlassianOAuth2.getAccessToken().catch(() => null));
    if (oauthToken) {
      const expiryInfo: TokenExpiryInfo = { isExpired: false, lastChecked: new Date().toISOString() };
      this.setCachedExpiry(expiryInfo);
      return { valid: true, expiryInfo };
    }

    if (!settings.domain || !settings.email || !settings.apiToken) {
      return {
        valid: false,
        error: {
          type: 'invalid',
          message: 'Jira credentials are incomplete'
        }
      };
    }

    // Check cache first
    const cached = this.getCachedExpiry();
    if (cached && !cached.isExpired) {
      return { valid: true, expiryInfo: cached };
    }

    try {
      const result = await validateJiraCredentials(settings);

      if (result.valid) {
        const expiryInfo: TokenExpiryInfo = {
          isExpired: false,
          lastChecked: new Date().toISOString()
        };
        this.setCachedExpiry(expiryInfo);
        return { valid: true, expiryInfo };
      } else {
        const errorType = this.detectExpiryType(result.error || 'Unknown error');
        const authError: JiraAuthError = {
          type: errorType,
          message: result.error || 'Authentication failed',
          originalError: result.error
        };

        const expiryInfo: TokenExpiryInfo = {
          isExpired: true,
          error: authError.message,
          lastChecked: new Date().toISOString()
        };
        this.setCachedExpiry(expiryInfo);

        // Notify listeners if it's an expiry issue
        if (errorType === 'expired') {
          this.notifyExpiry(authError);
        }

        return { valid: false, expiryInfo, error: authError };
      }
    } catch (err: any) {
      const errorType = this.detectExpiryType(err.message || 'Unknown error');
      const authError: JiraAuthError = {
        type: errorType,
        message: err.message || 'Validation failed',
        originalError: err.message
      };

      const expiryInfo: TokenExpiryInfo = {
        isExpired: true,
        error: authError.message,
        lastChecked: new Date().toISOString()
      };
      this.setCachedExpiry(expiryInfo);

      // Notify listeners if it's an expiry issue
      if (errorType === 'expired') {
        this.notifyExpiry(authError);
      }

      return { valid: false, expiryInfo, error: authError };
    }
  }

  // Quick check without network request (uses cache only)
  quickCheck(): TokenExpiryInfo | null {
    return this.getCachedExpiry();
  }

  // Force validation bypassing cache
  async forceValidation(settings: JiraCredentials): Promise<{
    valid: boolean;
    expiryInfo?: TokenExpiryInfo;
    error?: JiraAuthError;
  }> {
    this.clearCachedExpiry();
    return this.validateAndDetectExpiry(settings);
  }
}

// Export singleton instance
export const jiraTokenExpiryService = JiraTokenExpiryService.getInstance();

// Helper function to wrap Jira API calls with expiry detection
export async function withJiraExpiryCheck<T>(
  _settings: JiraCredentials,
  apiCall: () => Promise<T>
): Promise<{ result?: T; error?: JiraAuthError }> {
  try {
    const result = await apiCall();
    return { result };
  } catch (err: any) {
    const errorType = jiraTokenExpiryService['detectExpiryType'](err.message || 'Unknown error');
    const authError: JiraAuthError = {
      type: errorType,
      message: err.message || 'API call failed',
      originalError: err.message
    };

    // If it's an expiry error, update cache and notify
    if (errorType === 'expired') {
      const expiryInfo: TokenExpiryInfo = {
        isExpired: true,
        error: authError.message,
        lastChecked: new Date().toISOString()
      };
      jiraTokenExpiryService['setCachedExpiry'](expiryInfo);
      jiraTokenExpiryService['notifyExpiry'](authError);
    }

    return { error: authError };
  }
}
